import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Icons,
  iconVariants,
  Label,
  RadioGroup,
  RadioGroupItem,
  Textarea,
} from '@commenda/kakashi';
import { useState } from 'react';

interface RejectCertificateDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string, note: string) => void;
  isLoading: boolean;
}

const REJECTTION_REASONS = [
  'This certificate is expired',
  'The information is incorrect',
  'Not the right type of certificate',
  'Invalid documentation',
  'Other',
];

const RejectCertificateModal = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading,
}: RejectCertificateDialogProps) => {
  const [reason, setReason] = useState<string | null>(null);
  const [note, setNote] = useState('');

  const handleConfirm = () => {
    if (reason) {
      onConfirm(reason, note);
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="w-[600px] max-w-[95vw] flex flex-col p-0"
        hideCloseIcon
      >
        <DialogHeader className="border-b-0">
          <div className="flex flex-col items-start gap-4">
            <div className="flex items-center justify-center size-12 bg-bg-error rounded-lg">
              <Icons.InfoCircle
                className={iconVariants({ variant: 'warning' })}
              />
            </div>
            <div className="flex flex-col gap-1">
              <DialogTitle>Reject this certificate?</DialogTitle>
              <p className="txt-small-regular text-text-tertiary">
                Let the buyer know why this exemption certificate can&apos;t be
                accepted.
                <br />
                They&apos;ll be notified with your reason and can submit a
                corrected version.
              </p>
            </div>
          </div>
        </DialogHeader>

        <div className="flex flex-col gap-2xl p-2xl pt-0">
          <div className="flex flex-col gap-lg">
            <Label>
              Select a reason<span className="text-utility-violet-600">*</span>
            </Label>
            <RadioGroup
              value={reason || ''}
              onValueChange={setReason}
              className="flex flex-col gap-2"
            >
              {REJECTTION_REASONS.map((value) => (
                <div key={value} className="flex items-start gap-2">
                  <RadioGroupItem value={value} id={value} />
                  <Label htmlFor={value}>{value}</Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          <div className="flex flex-col gap-sm">
            <Label>Describe your issue</Label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="border-border-primary txt-small-regular text-text-secondary"
              placeholder="Provide clear details to help the buyer correct this certificate"
              rows={3}
            />
          </div>
        </div>

        <DialogFooter className="border-t border-border-primary">
          <div className="flex gap-md items-center justify-end">
            <Button variant="secondary" size="md" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="destructive-primary"
              size="md"
              onClick={handleConfirm}
              disabled={!reason || isLoading}
            >
              Reject certificate
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RejectCertificateModal;
